import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { BypassErrorHandling, BypassErrorToast } from '@app/v2/shared/helpers/http-context-tokens'
import { Response } from '@app/v2/shared/types/generic-response.type'
import { GenericTableSort, GenericTableSortDirection } from '@app/v2/shared/types/generic-table.types'
import { environment } from '@environments/environment'
import { map, Observable } from 'rxjs'
import { ChannelTemplate, ChannelTemplateChannel, PatchChannelTemplatePayload } from '../types/channel-template.type'
import {
  InvitationCollectionAddRequest,
  InvitationCollectionAddResult,
  InvitationCollectionContext,
  InvitationCollectionContextPayload,
  InvitationCollectionContextResponse,
  InvitationCollectionDeleteItemsPreviewResponse,
  InvitationCollectionDeleteItemsRequest,
  InvitationCollectionItemSource,
  InvitationCollectionItemSourceResponse,
  InvitationCollectionListResponse,
  InvitationCollectionQueryFilters,
  InvitationCollectionSendBulkRequest,
  InvitationCollectionSendBulkResponse,
  InvitationCollectionStatisticsResponse,
  SendInvitationHistoryResponse,
  SendInvitationPayload
} from '../types/invitation-tracker.type'

@Injectable()
export class InvitationTrackerService {
  private readonly _http = inject(HttpClient)

  /**
   * Imports invitees into the persona's invitation collection.
   * @param context {InvitationCollectionContext} - Organization, event, and persona slugs.
   * @param payload {InvitationCollectionContextPayload} - CSV file and column mapping.
   * @returns Observable with batch reference in `data` when available.
   */
  public importInvitees(context: InvitationCollectionContext, payload: InvitationCollectionContextPayload): Observable<Response<InvitationCollectionContextResponse>> {
    const formData = new FormData()
    formData.append('file', payload.file, payload.file.name)
    formData.append('data_mapping', JSON.stringify(payload.dataMapping))

    return this._http.post<Response<InvitationCollectionContextResponse>>(this._buildInvitationCollectionUrl(context, 'import'), formData, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Deletes a single invitee from the persona's invitation collection.
   * @param context {InvitationCollectionContext} - Organization, event, and persona slugs sent in request headers.
   * @param itemId {number} - Collection item ID.
   * @returns Observable with delete response.
   */
  public deleteCollectionItem(context: InvitationCollectionContext, itemId: number): Observable<Response<unknown>> {
    return this._http.delete<Response<unknown>>(this._buildInvitationCollectionUrl(context, `items/${itemId}`), {
      headers: this._buildBatchRequestHeaders(context),
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Previews how many selected collection items can be removed.
   * @param context {InvitationCollectionContext} - Organization, event, and persona slugs sent in request headers.
   * @param payload {InvitationCollectionDeleteItemsRequest} - Selection payload.
   * @returns Observable with preview counts.
   */
  public previewDeleteCollectionItems(
    context: InvitationCollectionContext,
    payload: InvitationCollectionDeleteItemsRequest
  ): Observable<InvitationCollectionDeleteItemsPreviewResponse> {
    return this._http.post<InvitationCollectionDeleteItemsPreviewResponse>(this._buildInvitationCollectionUrl(context, 'items/delete-items'), payload, {
      headers: this._buildBatchRequestHeaders(context),
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Deletes selected invitees from the persona's invitation collection.
   * @param context {InvitationCollectionContext} - Organization, event, and persona slugs sent in request headers.
   * @param payload {InvitationCollectionDeleteItemsRequest} - Selection payload.
   * @returns Observable with delete response.
   */
  public deleteCollectionItemsBulk(context: InvitationCollectionContext, payload: InvitationCollectionDeleteItemsRequest): Observable<Response<unknown>> {
    return this._http.delete<Response<unknown>>(this._buildInvitationCollectionUrl(context, 'items/delete-bulk'), {
      body: payload,
      headers: this._buildBatchRequestHeaders(context),
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Adds registrants from a source persona listing into a target event/persona invitation collection.
   * @param context {InvitationCollectionContext} - Source organization, event, and persona slugs.
   * @param payload {InvitationCollectionAddRequest} - Selection + target event/persona.
   * @returns Observable with eligible / skipped counts.
   */
  public addToInvitationCollection(context: InvitationCollectionContext, payload: InvitationCollectionAddRequest): Observable<InvitationCollectionAddResult> {
    return this._http.post<InvitationCollectionAddResult>(this._buildInvitationCollectionUrl(context, 'add'), payload, {
      headers: this._buildBatchRequestHeaders(context),
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Fetches paginated invitees from the persona's invitation collection.
   * @param context {InvitationCollectionContext} - Organization, event, and persona slugs sent in request headers.
   * @param page {number} - Page number (1-based).
   * @param perPage {number} - Number of items per page.
   * @param sort {Pick<GenericTableSort, 'field' | 'direction'> & { field: string }} - Optional sort column and direction.
   * @param search {string} - Optional search term.
   * @param filters {InvitationCollectionQueryFilters} - Optional filter query params (source, channel, invite_status, action_taken); each value is sent as `key[]=value`.
   * @returns Observable with collection list response.
   */
  public getInvitationCollection(
    context: InvitationCollectionContext,
    page: number = 1,
    perPage: number = 50,
    sort?: Pick<GenericTableSort, 'field' | 'direction'> & { field: string },
    search?: string,
    filters?: InvitationCollectionQueryFilters
  ): Observable<InvitationCollectionListResponse> {
    let url = `${this._buildInvitationCollectionUrl(context)}?page=${page}&per_page=${perPage}`

    if (sort?.field) {
      url += `&sort=${sort.direction || GenericTableSortDirection.DESC}`
      url += `&column=${encodeURIComponent(sort.field)}`
    }

    if (search) url += `&search=${encodeURIComponent(search)}`

    Object.entries(filters ?? {}).forEach(([key, values]) => {
      for (const value of values ?? []) url += `&${key}[]=${encodeURIComponent(String(value))}`
    })

    return this._http.get<InvitationCollectionListResponse>(url, {
      headers: this._buildBatchRequestHeaders(context),
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Loads source data (original field values + source event/persona) for a collection item.
   * @param context {InvitationCollectionContext} - Organization, event, and persona slugs sent in request headers.
   * @param itemId {number} - Collection item ID.
   * @returns Observable with the source item, or null when the API returns no rows.
   */
  public getInvitationCollectionItemSource(context: InvitationCollectionContext, itemId: number): Observable<InvitationCollectionItemSource | null> {
    return this._http
      .get<InvitationCollectionItemSourceResponse>(this._buildInvitationCollectionUrl(context, `${itemId}`), {
        headers: this._buildBatchRequestHeaders(context),
        context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
      })
      .pipe(map((response) => this._firstItemSource(response.data)))
  }

  /**
   * Loads saved invitation channel templates (email / WhatsApp / SMS) for a persona.
   * @param context {InvitationCollectionContext} - Organization, event, and persona slugs.
   * @returns Observable of channel template rows in `data`.
   */
  public getChannelTemplates(context: InvitationCollectionContext): Observable<Response<ChannelTemplate[]>> {
    return this._http.get<Response<ChannelTemplate[]>>(this._buildInvitationCollectionUrl(context, 'channel-templates'), {
      headers: this._buildBatchRequestHeaders(context),
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Saves (creates/updates) an invitation channel template for a persona.
   * @param context {InvitationCollectionContext} - Organization, event, and persona slugs.
   * @param payload {PatchChannelTemplatePayload} - Channel (`EMAIL` / `WHATSAPP`) + its `template_data`.
   * @returns Observable with the same list shape as GET.
   */
  public patchChannelTemplate(context: InvitationCollectionContext, payload: PatchChannelTemplatePayload): Observable<Response<ChannelTemplate[]>> {
    return this._http.patch<Response<ChannelTemplate[]>>(this._buildInvitationCollectionUrl(context, 'channel-templates'), payload, {
      headers: this._buildBatchRequestHeaders(context),
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Sends an individual invitation for a collection item on the given channel.
   * @param context {InvitationCollectionContext} - Organization, event, and persona slugs.
   * @param collectionItemId {number} - Invitation collection entry id.
   * @param payload {SendInvitationPayload} - Channel enum.
   * @returns Observable with API `message` (and optional `data`).
   */
  public sendInvitation(context: InvitationCollectionContext, collectionItemId: number, payload: SendInvitationPayload): Observable<Response<unknown>> {
    return this._http.post<Response<unknown>>(this._buildInvitationCollectionUrl(context, `send-invitation/${collectionItemId}`), payload, {
      headers: this._buildBatchRequestHeaders(context),
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Sends invitations in bulk for the selected invitation-collection items on the given channel.
   * @param context {InvitationCollectionContext} - Organization, event, and persona slugs.
   * @param payload {InvitationCollectionSendBulkRequest} - Selection payload plus channel.
   * @returns Observable with invited / skipped breakdown counts.
   */
  public sendInvitationBulk(context: InvitationCollectionContext, payload: InvitationCollectionSendBulkRequest): Observable<InvitationCollectionSendBulkResponse> {
    return this._http.post<InvitationCollectionSendBulkResponse>(this._buildInvitationCollectionUrl(context, 'send-invitation/bulk'), payload, {
      headers: this._buildBatchRequestHeaders(context),
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Loads paginated send-invitation history for a collection item and channel.
   * @param context {InvitationCollectionContext} - Organization, event, and persona slugs.
   * @param collectionItemId {number} - Invitation collection entry id.
   * @param channel {ChannelTemplateChannel} - Channel enum.
   * @param page {number} - Page number (1-based).
   * @param perPage {number} - Page size.
   * @returns Observable with `total_cost` and history `data` rows (`status`, `by`, `date`).
   */
  public getSendInvitationHistory(
    context: InvitationCollectionContext,
    collectionItemId: number,
    channel: ChannelTemplateChannel,
    page = 1,
    perPage = 5
  ): Observable<SendInvitationHistoryResponse> {
    const url = `${this._buildInvitationCollectionUrl(context, `send-invitation/${collectionItemId}`)}` + `?channel=${encodeURIComponent(channel)}&page=${page}&per_page=${perPage}`

    return this._http.get<SendInvitationHistoryResponse>(url, {
      headers: this._buildBatchRequestHeaders(context),
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Loads statistics for the invitation collection.
   * @param context {InvitationCollectionContext} - Organization, event, and persona slugs.
   * @returns Observable with the statistics.
   */
  public getInvitationStatistics(context: InvitationCollectionContext): Observable<InvitationCollectionStatisticsResponse> {
    return this._http.get<InvitationCollectionStatisticsResponse>(this._buildInvitationCollectionUrl(context, 'statistics'), {
      headers: this._buildBatchRequestHeaders(context),
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Returns the single source item from a detail response that may wrap it in an array.
   * @param data {InvitationCollectionItemSourceResponse['data']} - Response `data` (array or object).
   * @returns The source item, or null when empty.
   */
  private _firstItemSource(data: InvitationCollectionItemSourceResponse['data']): InvitationCollectionItemSource | null {
    if (Array.isArray(data)) return data[0] ?? null
    return data ?? null
  }

  /**
   * Builds the invitation collection API URL for the given context and suffix.
   * @param context {InvitationCollectionContext} - Organization, event, and persona slugs.
   * @param suffix {string} - API endpoint suffix.
   * @returns Full API URL.
   */
  private _buildInvitationCollectionUrl(context: InvitationCollectionContext, suffix?: string): string {
    const { organizationSlug, eventSlug, personaSlug } = context
    const base = `${environment.registrationsApi}organizations/${organizationSlug}/events/${eventSlug}/personas/${personaSlug}/invitation-collection`
    return suffix ? `${base}/${suffix}` : base
  }

  /**
   * Builds the request headers for the invitation collection API batch endpoint.
   * @param context {InvitationCollectionContext} - Organization, event, and persona slugs.
   * @returns HttpHeaders object.
   */
  private _buildBatchRequestHeaders(context: InvitationCollectionContext): HttpHeaders {
    const { organizationSlug, eventSlug, personaSlug } = context
    return new HttpHeaders({
      slugs: `${organizationSlug}/${eventSlug}/${personaSlug}`,
      'organization-slug': organizationSlug,
      'event-slug': eventSlug,
      'persona-slug': personaSlug
    })
  }
}
